import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import NavBar from "./NavBar";
import { pageStyles, palette } from "./pageStyles";
import { MARKET_API_BASE, marketFetch } from "../backendConfig";
import { makeSampleCandles } from "../backtest/sampleCandles";
import { STRATEGIES, defaultParams } from "../backtest/strategies";
import { buildCatalog, needsPython, positionsFor } from "../backtest/catalog";
import useLabStore from "../backtest/useLabStore";
import { AI_BACKTEST_SUMMARY_KEY } from "../backtest/labStore";
import StrategyLibrary from "./StrategyLibrary";
import { DEFAULT_SETTINGS, combinePortfolio, runBacktest } from "../backtest/engine";
import { computeMetrics, correlationMatrix, histogram, monthlyReturns, rollingPerformance } from "../backtest/metrics";
import { attributeTrades, monteCarloTrades, splitBacktest, splitPortfolioBacktest, walkForwardOptimize } from "../backtest/research";
import { EquityChart, MonthlyTable, RUN_COLORS, ReturnHistogram, TradesPriceChart } from "./BtCharts";
import { useAuth } from "../auth/AuthContext";
import { detectHMMRegimesCached } from "../Chart/hmmRegimes";

const TIMEFRAMES = ["1m", "5m", "15m", "30m", "1h", "4h", "1d"];
const SETTINGS_KEY = "lwt.bt.settings";
export const TRADES_KEY = "lwt.backtestTrades"; // read by the main chart
export const SHOW_ON_MAIN_KEY = "lwt.backtestShowOnMain";

const s = {
  body: { flex: 1, display: "flex", flexDirection: "column", gap: "14px", padding: "16px 24px 32px", minWidth: 0 },
  panel: { background: "linear-gradient(135deg, rgba(217, 239, 255, 0.11), rgba(125, 167, 207, 0.045))", backdropFilter: "blur(18px)", WebkitBackdropFilter: "blur(18px)", border: "1px solid rgba(210, 235, 255, 0.16)", borderRadius: "18px", boxShadow: "0 14px 38px rgba(0, 8, 20, 0.24), inset 0 1px 0 rgba(255,255,255,0.045)", padding: "16px", display: "flex", flexDirection: "column", gap: "10px", minWidth: 0 },
  row: { display: "flex", flexWrap: "wrap", gap: "8px", alignItems: "center" },
  small: { fontSize: "0.75rem", color: palette.mutedText },
  h: { margin: 0, fontSize: "0.95rem", fontWeight: 600 },
  field: { padding: "7px 9px", borderRadius: "4px", border: `1px solid ${palette.border}`, background: "#050608", color: palette.text, fontSize: "0.85rem", outline: "none" },
  btn: { padding: "9px 18px", borderRadius: "4px", border: "none", background: palette.accent, color: palette.accentText, fontWeight: 700, fontSize: "0.9rem", cursor: "pointer" },
  btnGhost: { padding: "7px 12px", borderRadius: "4px", border: `1px solid ${palette.border}`, background: "transparent", color: palette.text, fontSize: "0.8rem", cursor: "pointer" },
  grid2: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))", gap: "14px" },
  lbl: { display: "flex", flexDirection: "column", gap: "3px", fontSize: "0.72rem", color: palette.mutedText },
};

const fmtPct = (v, d = 2) => (Number.isFinite(v) ? `${(v * 100).toFixed(d)}%` : "-");
const fmtNum = (v, d = 2) => (v === Infinity ? "∞" : Number.isFinite(v) ? v.toFixed(d) : "-");
const fmtMoney = (v) => (Number.isFinite(v) ? v.toLocaleString(undefined, { maximumFractionDigits: 0 }) : "-");
const fmtTime = (t) => new Date(t * 1000).toISOString().replace("T", " ").slice(0, 16);

const STAT_ROWS = [
  ["Total return", (m) => fmtPct(m.totalReturn), (m) => m.totalReturn],
  ["CAGR", (m) => fmtPct(m.cagr), (m) => m.cagr],
  ["Sharpe", (m) => fmtNum(m.sharpe), (m) => m.sharpe],
  ["Sortino", (m) => fmtNum(m.sortino), (m) => m.sortino],
  ["Calmar", (m) => fmtNum(m.calmar), (m) => m.calmar],
  ["Max drawdown", (m) => fmtPct(m.maxDrawdown), () => -1],
  ["Longest drawdown (days)", (m) => fmtNum(m.longestDrawdownDays, 1)],
  ["Volatility (annual)", (m) => fmtPct(m.volatility)],
  ["Recovery factor", (m) => fmtNum(m.recoveryFactor)],
  ["End equity", (m) => fmtMoney(m.endEquity)],
  ["Trades", (m) => String(m.trades)],
  ["Long / short", (m) => `${m.longTrades} / ${m.shortTrades}`],
  ["Win rate", (m) => fmtPct(m.winRate, 1)],
  ["Profit factor", (m) => fmtNum(m.profitFactor)],
  ["Payoff (avg win / avg loss)", (m) => fmtNum(m.payoff)],
  ["Average win", (m) => fmtPct(m.avgWin)],
  ["Average loss", (m) => fmtPct(m.avgLoss)],
  ["Expectancy per trade", (m) => fmtPct(m.expectancy, 3), (m) => m.expectancy],
  ["Best trade", (m) => fmtPct(m.bestTrade)],
  ["Worst trade", (m) => fmtPct(m.worstTrade)],
  ["Average bars held", (m) => fmtNum(m.avgBars, 1)],
  ["Longest win / loss streak", (m) => `${m.maxWinStreak} / ${m.maxLossStreak}`],
  ["Time in market", (m) => fmtPct(m.exposure, 0)],
];

const SL_UNITS = [["off", "Off"], ["atr", "ATR ×"], ["pct", "%"]];
const TP_UNITS = [["off", "Off"], ["atr", "ATR ×"], ["pct", "%"], ["rr", "R:R"]];
const UNIT_LABEL = { atr: "× ATR", pct: "%", rr: "R", off: "" };
const SLIDER = {
  sl: { atr: [0.25, 8, 0.25], pct: [0.05, 3, 0.05] },
  tp: { atr: [0.25, 12, 0.25], pct: [0.05, 5, 0.05], rr: [0.25, 8, 0.25] },
};
const SCAN_GRID = {
  sl: { atr: [0, 0.5, 1, 1.5, 2, 2.5, 3, 4, 5], pct: [0, 0.1, 0.2, 0.3, 0.5, 0.75, 1, 1.5, 2] },
  tp: { atr: [0, 0.5, 1, 1.5, 2, 3, 4, 6, 8], pct: [0, 0.1, 0.2, 0.3, 0.5, 1, 1.5, 2, 3], rr: [0, 0.5, 1, 1.5, 2, 3, 4, 5, 6] },
};
const SCAN_METRICS = [
  ["calmar", "Calmar", (m) => m.calmar],
  ["sharpe", "Sharpe", (m) => m.sharpe],
  ["cagr", "CAGR", (m) => m.cagr],
  ["totalReturn", "Total return", (m) => m.totalReturn],
  ["maxDrawdown", "Max drawdown", (m) => m.maxDrawdown],
  ["profitFactor", "Profit factor", (m) => m.profitFactor],
  ["winRate", "Win rate", (m) => m.winRate],
];
const PCT_METRICS = new Set(["cagr", "totalReturn", "maxDrawdown", "winRate"]);
function defaultParameterRange(def, currentValue) {
  const current = Number(currentValue === "" || currentValue === null || currentValue === undefined ? def.value : currentValue);
  const center = Number.isFinite(current) ? current : Number(def.value) || 0;
  const step = Number.isFinite(Number(def.step)) && Number(def.step) > 0
    ? Number(def.step)
    : Number.isInteger(center) ? 1 : 0.1;
  const radius = step * 5;
  const min = def.min !== null && def.min !== undefined && def.min !== "" && Number.isFinite(Number(def.min)) ? Number(def.min) : center - radius;
  const max = def.max !== null && def.max !== undefined && def.max !== "" && Number.isFinite(Number(def.max)) ? Number(def.max) : center + radius;
  const round = (value) => Number(value.toPrecision(10));
  return {
    from: round(Math.max(min, center - radius)),
    to: round(Math.min(max, center + radius)),
    step,
  };
}
function scanParameterLabel(def, value) {
  const option = def?.options?.find((item) => item.value === value);
  if (option) return option.label;
  return typeof value === "number" && Number.isFinite(value)
    ? (Number.isInteger(value) ? String(value) : String(Number(value.toPrecision(6))))
    : String(value);
}
const hasRegimeSource = (rule) => ["long", "short", "exit"].some((side) =>
  (rule?.[side] || []).some((condition) => [condition.a, condition.b].some((ref) => /kaufman|efficiency|regime/i.test(String(ref?.src || ""))))
);

/** Turn the form values into engine settings (numbers, with sane limits). */
function buildCfg(settings) {
  const num = (v, d) => (Number.isFinite(Number(v)) && String(v).trim() !== "" ? Number(v) : d);
  const slType = ["atr", "pct"].includes(settings.slType) ? settings.slType : "off";
  const tpType = ["atr", "pct", "rr"].includes(settings.tpType) ? settings.tpType : "off";
  return {
    capital: Math.max(1, num(settings.capital, 10000)),
    allocPct: Math.min(1000, Math.max(1, num(settings.allocPct, 100))),
    spreadBps: Math.max(0, num(settings.spreadBps, 0)),
    commissionBps: Math.max(0, num(settings.commissionBps, 0)),
    slippageBps: Math.max(0, num(settings.slippageBps, 0)),
    entryOrderType: ["market", "limit", "stop"].includes(settings.entryOrderType) ? settings.entryOrderType : "market",
    entryOffsetBps: Math.min(10000, Math.max(0, num(settings.entryOffsetBps, 0))),
    longFinancingBpsPerDay: Math.min(1000, Math.max(-1000, num(settings.longFinancingBpsPerDay, 0))),
    shortFinancingBpsPerDay: Math.min(1000, Math.max(-1000, num(settings.shortFinancingBpsPerDay, 0))),
    slType,
    slValue: Math.max(0, num(settings.slValue, 2)),
    tpType,
    tpValue: Math.max(0, num(settings.tpValue, 4)),
    trailing: !!settings.trailing && slType !== "off",
    allowShort: !!settings.allowShort,
  };
}

function strategyMaxTrades(strategy, params) {
  if (!(strategy?.params || []).some((def) => def.key === "maxTrades")) return 1;
  const requested = Math.floor(Number(params?.maxTrades));
  return Number.isFinite(requested) ? Math.min(100, Math.max(1, requested)) : 1;
}

function strategyParams(st, params) {
  const p = {};
  (st.params || []).forEach((def) => {
    const raw = params[st.id]?.[def.key];
    if (def.type === "select") {
      p[def.key] = def.options?.some((option) => option.value === raw) ? raw : def.value;
      return;
    }
    const v = Number(raw);
    // built-in settings are periods/multipliers (> 0); your own param() settings may be zero or negative
    const okValue = raw !== "" && raw !== undefined && Number.isFinite(v) && (st.kind !== "builtin" || v > 0);
    p[def.key] = okValue ? v : def.value;
  });
  return p;
}

/** Settings for every coded item (rules read their indicators' settings from here). */
function allItemParams(items, params) {
  const out = {};
  items.forEach((it) => {
    out[it.id] = strategyParams({ ...it, kind: "code" }, params);
  });
  return out;
}

function loadSaved() {
  try {
    const raw = JSON.parse(window.localStorage.getItem(SETTINGS_KEY) || "null");
    return raw && typeof raw === "object" ? raw : {};
  } catch {
    return {};
  }
}

function cleanCandles(rows) {
  const seen = new Set();
  return rows
    .filter((c) => c && [c.time, c.open, c.high, c.low, c.close].every((v) => Number.isFinite(v)))
    .sort((a, b) => a.time - b.time)
    .filter((c) => !seen.has(c.time) && seen.add(c.time));
}

const DEFAULT_BARS = 1000;
const MAX_BACKTEST_BARS = 25000;
const BACKTEST_CANDLE_PAGE = 5000;
async function fetchBacktestHistory(symbol, timeframe, requested, onProgress) {
  let candles = [];
  let end = null;
  while (candles.length < requested) {
    const params = new URLSearchParams({
      symbol,
      tf: timeframe,
      limit: String(Math.min(BACKTEST_CANDLE_PAGE, requested)),
    });
    if (end !== null) params.set("to", String(end));
    const res = await marketFetch(`${MARKET_API_BASE}/candles?${params.toString()}`);
    const data = await res.json().catch(() => null);
    if (!res.ok || !Array.isArray(data)) throw new Error((data && data.detail) || `Server answered ${res.status}`);

    const page = cleanCandles(data);
    if (!page.length) break;
    const previousOldest = candles[0]?.time;
    const merged = cleanCandles([...page, ...candles]).slice(-requested);
    if (previousOldest !== undefined && merged[0]?.time >= previousOldest) break;
    candles = merged;
    onProgress?.(candles.length, requested);
    if (candles.length >= requested) break;

    const earliest = page[0].time;
    if (previousOldest !== undefined && earliest >= previousOldest) break;
    end = earliest - 1;
  }
  return candles;
}
const SAMPLE_NOTE =
  "Showing 1,000 sample candles (made-up, EURUSD-like, 1 hour) so you can try a backtest straight away. Press Load candles for real market data.";

export default function BacktestPage({ embedded = false, onOpenEditor = () => {} }) {
  const { user } = useAuth();
  const isAdmin = Boolean(user?.is_admin);
  const saved = useMemo(loadSaved, []);
  const [symbol, setSymbol] = useState(saved.symbol || "EURUSD");
  const [tf, setTf] = useState(saved.tf || "1h");
  // 3000 was the old default: move people still on it to the new 1,000 default
  const [limit, setLimit] = useState(saved.limit && Number(saved.limit) !== 3000 ? saved.limit : DEFAULT_BARS);
  const [candles, setCandles] = useState(() => makeSampleCandles({ count: DEFAULT_BARS }));
  const [isSample, setIsSample] = useState(true); // sample trades are never sent to the main chart
  const [runAfterLoad, setRunAfterLoad] = useState(false); // set when the first automatic load succeeds
  const [dataNote, setDataNote] = useState(SAMPLE_NOTE);
  const [dataLoading, setDataLoading] = useState(false);

  const [selected, setSelected] = useState(saved.selected || { sma_cross: true, supertrend_flip: true, donchian_breakout: true });
  const [params, setParams] = useState(() => {
    const out = {};
    STRATEGIES.forEach((st) => {
      out[st.id] = { ...defaultParams(st), ...((saved.params || {})[st.id] || {}) };
    });
    return out;
  });
  const { items: labItems, rules: labRules } = useLabStore();
  const catalog = useMemo(
    () => buildCatalog(labItems, labRules).filter((entry) =>
      (isAdmin || !entry.adminOnly) &&
      (isAdmin || entry.kind !== "rule" || !hasRegimeSource(entry.rule))
    ),
    [isAdmin, labItems, labRules]
  );
  const [running, setRunning] = useState("");
  const runTokenRef = useRef(0);
  const hasRunRef = useRef(false);
  const [settings, setSettings] = useState(() => {
    const merged = { ...DEFAULT_SETTINGS, ...(saved.settings || {}) };
    if (saved.settings && saved.settings.atrStop > 0 && !saved.settings.slType) {
      merged.slType = "atr";
      merged.slValue = saved.settings.atrStop;
    }
    return merged;
  });
  const [live, setLive] = useState(true);
  const [scanStrategy, setScanStrategy] = useState("");
  const [scanMetric, setScanMetric] = useState("calmar");
  const [scanSl, setScanSl] = useState("atr");
  const [scanTp, setScanTp] = useState("rr");
  const [scanParamKey, setScanParamKey] = useState("");
  const [scanFrom, setScanFrom] = useState("");
  const [scanTo, setScanTo] = useState("");
  const [scanStep, setScanStep] = useState("");
  const [scan, setScan] = useState(null);
  const [scanRunning, setScanRunning] = useState(false);
  const [scanProgress, setScanProgress] = useState("");
  const [scanError, setScanError] = useState("");
  const [walkForwardMetric, setWalkForwardMetric] = useState("calmar");
  const [walkForwardState, setWalkForwardState] = useState(null);
  const [walkForwardRunning, setWalkForwardRunning] = useState(false);
  const [walkForwardProgress, setWalkForwardProgress] = useState("");
  const [walkForwardError, setWalkForwardError] = useState("");

  const [result, setResult] = useState(null);
  const [validationTrainPct, setValidationTrainPct] = useState(70);
  const [validationState, setValidationState] = useState(null);
  const [validationBusy, setValidationBusy] = useState(false);
  const [monteCarloState, setMonteCarloState] = useState(null);
  const [monteCarloBusy, setMonteCarloBusy] = useState(false);
  const [focusId, setFocusId] = useState("portfolio");
  const [zoomTrade, setZoomTrade] = useState(null);
  const [error, setError] = useState("");
  const [showOnMain, setShowOnMain] = useState(() => {
    try {
      return window.localStorage.getItem(SHOW_ON_MAIN_KEY) !== "0";
    } catch {
      return true;
    }
  });

  const scanStrategyId = result?.runs?.some((run) => run.id === scanStrategy)
    ? scanStrategy
    : result?.runs?.[0]?.id || "";
  const scanEntry = catalog.find((entry) => entry.id === scanStrategyId);
  const scanParamDefs = (scanEntry?.params || []).filter((def) =>
    def.type === "select" ? (def.options || []).length > 0 : Number.isFinite(Number(def.value))
  );
  const scanParamDef = scanParamDefs.find((def) => def.key === scanParamKey) || scanParamDefs[0] || null;
  const scanRangeDefaults = scanParamDef && scanParamDef.type !== "select"
    ? defaultParameterRange(scanParamDef, params[scanStrategyId]?.[scanParamDef.key])
    : null;
  const walkForward = walkForwardState?.source === result && walkForwardState?.strategyId === scanStrategyId && walkForwardState?.parameterKey === scanParamDef?.key && walkForwardState?.metric === walkForwardMetric
    ? walkForwardState.data
    : null;

  useEffect(() => {
    const clearFromChart = () => setShowOnMain(false);
    window.addEventListener("lwt:backtest-overlay-cleared", clearFromChart);
    return () => window.removeEventListener("lwt:backtest-overlay-cleared", clearFromChart);
  }, []);

  useEffect(() => {
    setZoomTrade(null);
  }, [focusId]);

  useEffect(() => {
    try {
      window.localStorage.setItem(SETTINGS_KEY, JSON.stringify({ symbol, tf, limit, selected, params, settings }));
    } catch {
      // storage unavailable
    }
  }, [symbol, tf, limit, selected, params, settings]);

  const loadCandles = useCallback(async (auto) => {
    const sym = symbol.trim().toUpperCase();
    if (!sym) return;
    setDataLoading(true);
    setError("");
    setDataNote("Loading (the free server can take about a minute to wake up)...");
    try {
      const requested = Math.min(MAX_BACKTEST_BARS, Math.max(50, Math.floor(Number(limit) || DEFAULT_BARS)));
      const rows = await fetchBacktestHistory(sym, tf, requested, (loaded, total) => {
        setDataNote(`Loading history: ${loaded.toLocaleString()} of ${total.toLocaleString()} candles…`);
      });
      if (!rows.length) {
        setDataNote(`No candles came back for ${sym} ${tf}. The market may be closed: try a longer timeframe or more bars. Keeping the current candles.`);
        return;
      }
      setCandles(rows);
      setIsSample(false);
      if (auto === true) setRunAfterLoad(true); // opened the page: show results on the real candles straight away
      setResult(null);
      setScan(null);
      hasRunRef.current = false;
      setDataNote(
        rows.length
          ? `Loaded ${rows.length.toLocaleString()} ${sym} ${tf} candles (${fmtTime(rows[0].time).slice(0, 10)} to ${fmtTime(rows[rows.length - 1].time).slice(0, 10)})${rows.length < requested ? `; history source returned fewer than the ${requested.toLocaleString()} requested.` : "."}`
          : `No candles came back for ${sym} ${tf}. The market may be closed: try a longer timeframe or more bars.`
      );
    } catch (err) {
      // keep whatever candles are already loaded (the sample ones at first) so the page stays usable
      setDataNote(
        auto === true
          ? `Live data isn't reachable right now (${err.message}). ${SAMPLE_NOTE}`
          : `Could not load candles: ${err.message}. Keeping the current candles.`
      );
    } finally {
      setDataLoading(false);
    }
  }, [symbol, tf, limit]);

  const executeRun = useCallback(
    async (keepFocus) => {
      setError("");
      const chosen = catalog.filter((st) => selected[st.id]);
      if (candles.length < 60) {
        setError("Load at least 60 candles first.");
        return;
      }
      if (!chosen.length) {
        setError("Tick at least one strategy.");
        return;
      }
      const cfg = buildCfg(settings);
      runTokenRef.current += 1;
      const token = runTokenRef.current; // a newer run (live dashboard) makes this one's result stale
      const ctx = { items: labItems, paramsById: allItemParams(labItems, params) };
      try {
        const desiredList = [];
        const skipped = [];
        for (const st of chosen) {
          if (needsPython(st)) setRunning(`Running ${st.name} with Python (the first run loads Python, about 10-20 s)...`);
          try {
            const currentParams = strategyParams(st, params);
            const maxTrades = strategyMaxTrades(st, currentParams);
            desiredList.push({
              st,
              desired: await positionsFor(st, candles, currentParams, ctx),
              maxTrades,
            });
          } catch (err) {
            skipped.push(`${st.name}: ${err.message}`);
          }
          if (token !== runTokenRef.current) return;
        }
        setRunning("");
        if (!desiredList.length) {
          setError(`Nothing could run. ${skipped.join(" ")}`);
          return;
        }
        if (skipped.length) setError(`Skipped ${skipped.join(" | ")}`);
        const runs = desiredList.map(({ st, desired, maxTrades }, idx) => {
          const run = runBacktest(candles, desired, { ...cfg, maxTrades });
          run.id = st.id;
          run.name = st.name;
          run.desired = desired; // kept for the stop/target scan (no need to run Python again)
          run.color = RUN_COLORS[idx % RUN_COLORS.length];
          run.metrics = computeMetrics(run, cfg.capital);
          run.months = monthlyReturns(run.times, run.equity, cfg.capital);
          return run;
        });
        let portfolio = null;
        if (runs.length > 1) {
          portfolio = combinePortfolio(runs, cfg.capital);
          portfolio.id = "portfolio";
          portfolio.name = "Portfolio";
          portfolio.color = "#ffffff";
          portfolio.bold = true;
          portfolio.metrics = computeMetrics(portfolio, cfg.capital);
          portfolio.months = monthlyReturns(portfolio.times, portfolio.equity, cfg.capital);
        }
        const first = candles[0].close;
        const bh = {
          id: "buyhold",
          name: "Buy & hold",
          color: "#9097a3",
          dashed: true,
          times: candles.map((c) => c.time),
          equity: candles.map((c) => (cfg.capital * c.close) / first),
          position: candles.map(() => 1),
          trades: [],
        };
        bh.metrics = computeMetrics(bh, cfg.capital);
        const corr = runs.length > 1 ? correlationMatrix(runs) : null;
        setResult({ runs, portfolio, bh, corr, cfg, candles, symbol: isSample ? "SAMPLE" : symbol.trim().toUpperCase(), tf: isSample ? "1h" : tf, sample: isSample });
        setFocusId((prev) => {
          const valid = (prev === "portfolio" && portfolio) || runs.some((r) => r.id === prev);
          return keepFocus && valid ? prev : portfolio ? "portfolio" : runs[0].id;
        });
        if (!keepFocus) setZoomTrade(null);
      } catch (err) {
        setRunning("");
        setError(`Backtest failed: ${err.message}`);
      }
    },
    [catalog, labItems, candles, selected, params, settings, symbol, tf, isSample]
  );
  const runAll = useCallback(() => executeRun(false), [executeRun]);

  const navigate = useNavigate();
  /** Open the main chart on the backtested symbol + timeframe so it draws these trades.
   *  The chart restores "lwt.lastLayout" when it opens, so pointing that at the result is enough. */
  const openOnMainChart = () => {
    if (!result || result.sample) return;
    setShowOnMain(true);
    try {
      const layout = JSON.parse(window.localStorage.getItem("lwt.lastLayout") || "null") || {};
      window.localStorage.setItem(
        "lwt.lastLayout",
        JSON.stringify({ ...layout, marketSymbol: result.symbol, chartTimeframeId: result.tf, liveRatioSymbols: null })
      );
      window.localStorage.setItem(SHOW_ON_MAIN_KEY, "1");
    } catch {
      // storage unavailable: the chart opens on its last symbol instead
    }
    navigate("/app#chart");
  };

  const switchToSample = () => {
    setCandles(makeSampleCandles({ count: DEFAULT_BARS }));
    setIsSample(true);
    setResult(null);
    setScan(null);
    hasRunRef.current = false;
    setDataNote(SAMPLE_NOTE);
  };

  // first visit: run the ticked strategies on the sample candles so results show right away,
  // then try to load the default 1,000 real candles and re-run on those when they arrive
  const autoRanRef = useRef(false);
  const autoLoadedRef = useRef(false);
  useEffect(() => {
    if (autoRanRef.current) return;
    autoRanRef.current = true;
    executeRun(false);
  }, [executeRun]);
  useEffect(() => {
    if (autoLoadedRef.current) return;
    autoLoadedRef.current = true;
    loadCandles(true);
  }, [loadCandles]);
  useEffect(() => {
    if (!runAfterLoad) return;
    setRunAfterLoad(false);
    executeRun(false);
  }, [runAfterLoad, executeRun]);

  // live dashboard: every change to the settings, strategies or parameters re-runs the backtest
  useEffect(() => {
    if (!live || !hasRunRef.current) return undefined;
    const timer = setTimeout(() => executeRun(true), 250);
    return () => clearTimeout(timer);
  }, [live, executeRun]);
  useEffect(() => {
    if (result) hasRunRef.current = true;
  }, [result]);

  const runScan = useCallback(() => {
    if (!result) return;
    const st = result.runs.find((x) => x.id === scanStrategy) || result.runs[0];
    const desired = st.desired;
    const base = buildCfg(settings);
    const slVals = SCAN_GRID.sl[scanSl];
    const tpVals = SCAN_GRID.tp[scanTp];
    const getter = SCAN_METRICS.find((m) => m[0] === scanMetric)[2];
    const cells = slVals.map((sl) =>
      tpVals.map((tp) => {
        // R:R targets need a stop loss to measure against
        if (scanTp === "rr" && sl === 0 && tp > 0) return null;
        const run = runBacktest(candles, desired, {
          ...base,
          maxTrades: st.settings?.maxTrades || 1,
          slType: sl > 0 ? scanSl : "off",
          slValue: sl,
          tpType: tp > 0 ? scanTp : "off",
          tpValue: tp,
          trailing: sl > 0 && base.trailing,
        });
        const m = computeMetrics(run, base.capital);
        const v = m ? getter(m) : NaN;
        return Number.isFinite(v) ? v : null;
      })
    );
    setScan({ mode: "risk", strategy: st.name, metric: scanMetric, slUnit: scanSl, tpUnit: scanTp, slVals, tpVals, cells });
  }, [result, scanStrategy, scanMetric, scanSl, scanTp, candles, settings]);

  const runParameterScan = useCallback(async () => {
    if (!isAdmin || !result || !scanEntry || !scanParamDef) return;
    setScanError("");
    setScan(null);

    let values;
    if (scanParamDef.type === "select") {
      values = (scanParamDef.options || []).map((option) => option.value);
    } else {
      const defaults = defaultParameterRange(scanParamDef, params[scanStrategyId]?.[scanParamDef.key]);
      const from = Number(scanFrom === "" ? defaults.from : scanFrom);
      const to = Number(scanTo === "" ? defaults.to : scanTo);
      const step = Number(scanStep === "" ? defaults.step : scanStep);
      const min = scanParamDef.min !== null && scanParamDef.min !== undefined && scanParamDef.min !== "" && Number.isFinite(Number(scanParamDef.min)) ? Number(scanParamDef.min) : -Infinity;
      const max = scanParamDef.max !== null && scanParamDef.max !== undefined && scanParamDef.max !== "" && Number.isFinite(Number(scanParamDef.max)) ? Number(scanParamDef.max) : Infinity;
      if (!Number.isFinite(from) || !Number.isFinite(to) || !Number.isFinite(step) || step <= 0 || from > to) {
        setScanError("Enter a valid range with From ≤ To and a positive step.");
        return;
      }
      if (from < min || to > max) {
        setScanError(`Values must stay between ${Number.isFinite(min) ? min : "−∞"} and ${Number.isFinite(max) ? max : "∞"}.`);
        return;
      }
      const count = Math.floor((to - from) / step + 1e-9) + 1;
      if (count > 51) {
        setScanError("Choose a smaller range or larger step. A scan is limited to 51 values.");
        return;
      }
      values = Array.from({ length: count }, (_, index) => Number((from + index * step).toPrecision(12)));
    }

    if (!values.length) {
      setScanError("This parameter has no values to scan.");
      return;
    }

    const cfg = buildCfg(settings);
    const baseParams = strategyParams(scanEntry, params);
    const ctx = { items: labItems, paramsById: allItemParams(labItems, params) };
    const rows = [];
    setScanRunning(true);
    try {
      for (let index = 0; index < values.length; index += 1) {
        const value = values[index];
        setScanProgress(`Testing ${scanParameterLabel(scanParamDef, value)} (${index + 1}/${values.length})…`);
        try {
          await new Promise((resolve) => window.setTimeout(resolve, 0));
          const candidateParams = { ...baseParams, [scanParamDef.key]: value };
          const desired = await positionsFor(scanEntry, candles, candidateParams, ctx);
          const maxTrades = strategyMaxTrades(scanEntry, candidateParams);
          const run = runBacktest(candles, desired, { ...cfg, maxTrades });
          const metrics = computeMetrics(run, cfg.capital);
          rows.push({ value, sharpe: metrics?.sharpe, calmar: metrics?.calmar, trades: metrics?.trades ?? 0 });
        } catch (err) {
          rows.push({ value, sharpe: NaN, calmar: NaN, trades: 0, error: err?.message || "This parameter value failed." });
        }
      }
      setScan({
        mode: "parameter",
        strategy: scanEntry.name,
        strategyId: scanEntry.id,
        parameter: scanParamDef,
        rows,
      });
    } catch (err) {
      setScanError(err?.message || "The parameter scan could not be completed.");
    } finally {
      setScanRunning(false);
      setScanProgress("");
    }
  }, [isAdmin, result, scanEntry, scanParamDef, params, scanStrategyId, scanFrom, scanTo, scanStep, settings, labItems, candles]);

  const runWalkForward = useCallback(async () => {
    if (!isAdmin || !result || !scanEntry || !scanParamDef) return;
    setWalkForwardError("");
    setWalkForwardState(null);
    let values;
    if (scanParamDef.type === "select") {
      values = (scanParamDef.options || []).map((option) => option.value);
    } else {
      const defaults = defaultParameterRange(scanParamDef, params[scanStrategyId]?.[scanParamDef.key]);
      const from = Number(scanFrom === "" ? defaults.from : scanFrom);
      const to = Number(scanTo === "" ? defaults.to : scanTo);
      const step = Number(scanStep === "" ? defaults.step : scanStep);
      const min = Number.isFinite(Number(scanParamDef.min)) ? Number(scanParamDef.min) : -Infinity;
      const max = Number.isFinite(Number(scanParamDef.max)) ? Number(scanParamDef.max) : Infinity;
      if (!Number.isFinite(from) || !Number.isFinite(to) || !Number.isFinite(step) || step <= 0 || from > to || from < min || to > max) {
        setWalkForwardError("Enter a valid parameter range within its allowed limits.");
        return;
      }
      const count = Math.floor((to - from) / step + 1e-9) + 1;
      if (count > 31) {
        setWalkForwardError("Walk-forward is limited to 31 parameter values per fold.");
        return;
      }
      values = Array.from({ length: count }, (_, index) => Number((from + index * step).toPrecision(12)));
    }
    if (values.length < 2) {
      setWalkForwardError("Choose a parameter with at least two values to compare.");
      return;
    }

    const cfg = buildCfg(settings);
    const baseParams = strategyParams(scanEntry, params);
    const ctx = { items: labItems, paramsById: allItemParams(labItems, params) };
    setWalkForwardRunning(true);
    try {
      const data = await walkForwardOptimize({
        candles,
        parameterValues: values,
        parameterKey: scanParamDef.key,
        baseParams,
        settings: cfg,
        metric: walkForwardMetric,
        folds: 3,
        getPositions: (rows, candidateParams) => positionsFor(scanEntry, rows, candidateParams, ctx),
        getSettings: (candidateParams) => ({
          ...cfg,
          maxTrades: strategyMaxTrades(scanEntry, candidateParams),
        }),
        onProgress: ({ fold, folds, candidate, candidates }) => setWalkForwardProgress(`Fold ${fold}/${folds} · value ${candidate}/${candidates}`),
      });
      setWalkForwardState({ source: result, strategyId: scanEntry.id, parameterKey: scanParamDef.key, metric: walkForwardMetric, data });
    } catch (err) {
      setWalkForwardError(err?.message || "Walk-forward optimization failed.");
    } finally {
      setWalkForwardRunning(false);
      setWalkForwardProgress("");
    }
  }, [isAdmin, result, scanEntry, scanParamDef, params, scanStrategyId, scanFrom, scanTo, scanStep, settings, labItems, candles, walkForwardMetric]);

  const focus = useMemo(() => {
    if (!result) return null;
    if (focusId === "portfolio" && result.portfolio) return result.portfolio;
    return result.runs.find((r) => r.id === focusId) || result.runs[0];
  }, [result, focusId]);

  useEffect(() => {
    if (!result || !focus?.metrics) return;
    const metrics = focus.metrics;
    const summary = {
      symbol: result.symbol,
      timeframe: result.tf,
      bars: result.candles.length,
      sample_data: Boolean(result.sample),
      strategy: focus.name,
      starting_equity: result.cfg.capital,
      period_start: result.candles[0]?.time ?? null,
      period_end: result.candles[result.candles.length - 1]?.time ?? null,
      metrics: Object.fromEntries(
        ["totalReturn", "cagr", "sharpe", "sortino", "calmar", "maxDrawdown", "trades", "longTrades", "shortTrades", "winRate", "profitFactor", "endEquity"]
          .filter((key) => Number.isFinite(metrics[key]))
          .map((key) => [key, metrics[key]])
      ),
      note: "Historical backtest summary; not a forecast.",
    };
    try {
      window.localStorage.setItem(AI_BACKTEST_SUMMARY_KEY, JSON.stringify(summary));
    } catch {
      // Results remain available on the Backtesting page if browser storage is unavailable.
    }
  }, [result, focus]);
  const hmmRegimeModel = useMemo(() => result ? detectHMMRegimesCached(result.candles) : null, [result]);
  const attribution = useMemo(() => result && focus
    ? attributeTrades(result.candles, focus.trades || [], { hmmResult: hmmRegimeModel })
    : null, [result, focus, hmmRegimeModel]);
  const rolling = useMemo(() => result && focus ? rollingPerformance(focus.times, focus.equity, result.cfg.capital) : [], [result, focus]);

  const validation = validationState?.source === result && validationState?.focusId === focus?.id && validationState?.trainPct === validationTrainPct
    ? validationState.data
    : null;
  const validationError = validationState?.source === result && validationState?.focusId === focus?.id && validationState?.trainPct === validationTrainPct
    ? validationState.error
    : null;
  const runValidation = () => {
    if (!result || !focus) return;
    const source = result;
    const sourceFocus = focus;
    const trainPct = validationTrainPct;
    setValidationBusy(true);
    window.setTimeout(() => {
      try {
        const data = sourceFocus.id === "portfolio" && source.runs.length > 1
          ? splitPortfolioBacktest(source.candles, source.runs, source.cfg, trainPct)
          : splitBacktest(source.candles, sourceFocus.desired, { ...source.cfg, ...(sourceFocus.settings || {}) }, trainPct);
        setValidationState({ source, focusId: sourceFocus.id, trainPct, data });
      } catch (error) {
        setValidationState({ source, focusId: sourceFocus.id, trainPct, error: error?.message || "The split check could not be completed." });
      } finally {
        setValidationBusy(false);
      }
    }, 0);
  };
  const monteCarlo = monteCarloState?.source === result && monteCarloState?.focusId === focus?.id
    ? monteCarloState.data
    : null;

  const runMonteCarlo = () => {
    if (!focus || !result) return;
    setMonteCarloBusy(true);
    window.setTimeout(() => {
      const data = monteCarloTrades(focus.trades, result.cfg.capital, { paths: 500, seed: 42 });
      setMonteCarloState({ source: result, focusId: focus.id, data });
      setMonteCarloBusy(false);
    }, 0);
  };

  const curves = useMemo(() => {
    if (!result) return [];
    return [...result.runs, ...(result.portfolio ? [result.portfolio] : []), result.bh];
  }, [result]);

  const columns = useMemo(() => (result ? [...result.runs, ...(result.portfolio ? [result.portfolio] : []), result.bh] : []), [result]);

  // hand the trades to the main chart
  useEffect(() => {
    try {
      window.localStorage.setItem(SHOW_ON_MAIN_KEY, showOnMain ? "1" : "0");
      if (result && result.sample) {
        // trades on made-up candles mean nothing on the real chart
        window.localStorage.removeItem(TRADES_KEY);
      } else if (result && focus && showOnMain) {
        const trades = (focus.trades || []).slice(-3000).map((t) => ({ t: t.entryTime, x: t.exitTime, s: t.side, r: t.ret, a: t.entryPrice, b: t.exitPrice, e: t.reason }));
        window.localStorage.setItem(
          TRADES_KEY,
          JSON.stringify({ symbol: result.symbol, tf: result.tf, name: focus.name, savedAt: Date.now(), trades })
        );
      } else if (!showOnMain) {
        window.localStorage.removeItem(TRADES_KEY);
      }
      window.dispatchEvent(new Event("lwt:backtest-overlay-updated"));
    } catch {
      // storage unavailable
    }
  }, [result, focus, showOnMain]);

  const bins = useMemo(() => (focus ? histogram((focus.trades || []).map((t) => t.ret), 24) : []), [focus]);
  const rBins = useMemo(() => (focus ? histogram((focus.trades || []).map((t) => t.rMultiple).filter((v) => Number.isFinite(v)), 24) : []), [focus]);

  const downloadCsv = () => {
    if (!focus) return;
    const head = "strategy,side,entry_time,entry_price,exit_time,exit_price,return_pct,r_multiple,pnl,bars,exit_reason";
    const rows = (focus.trades || []).map((t) =>
      [t.strategy || focus.name, t.side === 1 ? "long" : "short", fmtTime(t.entryTime), t.entryPrice, fmtTime(t.exitTime), t.exitPrice, (t.ret * 100).toFixed(4), Number.isFinite(t.rMultiple) ? t.rMultiple.toFixed(3) : "", t.pnl.toFixed(2), t.bars, t.reason].join(",")
    );
    const blob = new Blob([[head, ...rows].join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `trades_${result.symbol}_${result.tf}_${focus.name.replace(/\W+/g, "_")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const setParam = (id, key, value) => setParams((prev) => ({ ...prev, [id]: { ...prev[id], [key]: value } }));
  const setSetting = (key, value) => setSettings((prev) => ({ ...prev, [key]: value }));
  const tradeRows = focus ? [...(focus.trades || [])].slice(-300).reverse() : [];
  const parameterScanRows = scan?.mode === "parameter" ? scan.rows : [];
  const bestScanRow = (metric) => parameterScanRows
    .filter((row) => Number.isFinite(row[metric]))
    .reduce((best, row) => !best || row[metric] > best[metric] ? row : best, null);
  const bestSharpeScan = bestScanRow("sharpe");
  const bestCalmarScan = bestScanRow("calmar");
  const applyScanValue = (value) => setParams((prev) => ({
    ...prev,
    [scan.strategyId]: { ...prev[scan.strategyId], [scan.parameter.key]: value },
  }));
  const adminScanControls = isAdmin ? (
    <div style={{ display: "flex", flexDirection: "column", gap: 9, marginTop: 10, paddingTop: 10, borderTop: `1px solid ${palette.border}` }}>
      <div style={s.row}>
        <strong style={{ fontSize: "0.82rem", marginRight: 4 }}>Admin parameter scan</strong>
        <label style={s.lbl}>
          Strategy
          <select style={s.field} value={scanStrategyId} disabled={!result?.runs?.length || scanRunning} onChange={(e) => {
            setScanStrategy(e.target.value);
            setScanParamKey("");
            setScanFrom("");
            setScanTo("");
            setScanStep("");
            setScan(null);
            setScanError("");
          }}>
            {(result?.runs || []).map((run) => <option key={run.id} value={run.id}>{run.name}</option>)}
          </select>
        </label>
        <label style={s.lbl}>
          Parameter
          <select style={s.field} value={scanParamDef?.key || ""} disabled={!scanParamDefs.length || scanRunning} onChange={(e) => {
            setScanParamKey(e.target.value);
            setScanFrom("");
            setScanTo("");
            setScanStep("");
            setScan(null);
            setScanError("");
          }}>
            {scanParamDefs.map((def) => <option key={def.key} value={def.key}>{def.label || def.key}</option>)}
          </select>
        </label>
        {scanParamDef?.type === "select" ? (
          <span style={{ ...s.small, alignSelf: "flex-end" }}>Tests each available choice.</span>
        ) : scanParamDef && scanRangeDefaults ? (
          <>
            <label style={s.lbl}>From<input style={{ ...s.field, width: 82 }} type="number" disabled={scanRunning} min={scanParamDef.min} max={scanParamDef.max} step={scanParamDef.step || "any"} value={scanFrom === "" ? scanRangeDefaults.from : scanFrom} onChange={(e) => setScanFrom(e.target.value)} /></label>
            <label style={s.lbl}>To<input style={{ ...s.field, width: 82 }} type="number" disabled={scanRunning} min={scanParamDef.min} max={scanParamDef.max} step={scanParamDef.step || "any"} value={scanTo === "" ? scanRangeDefaults.to : scanTo} onChange={(e) => setScanTo(e.target.value)} /></label>
            <label style={s.lbl}>Step<input style={{ ...s.field, width: 82 }} type="number" disabled={scanRunning} min="0.000001" step="any" value={scanStep === "" ? scanRangeDefaults.step : scanStep} onChange={(e) => setScanStep(e.target.value)} /></label>
          </>
        ) : (
          <span style={{ ...s.small, alignSelf: "flex-end" }}>
            {result ? "This strategy has no adjustable parameters to scan." : "Run a backtest first to populate strategies here."}
          </span>
        )}
        <button type="button" style={{ ...s.btn, padding: "8px 12px", alignSelf: "flex-end" }} onClick={runParameterScan} disabled={!result || !scanParamDef || scanRunning || !!running}>
          {scanRunning ? "Scanning…" : "Run parameter scan"}
        </button>
      </div>
      <div style={s.small}>Uses the current candles, costs, stop loss and take profit. Scans are limited to 51 values.</div>
      {scanProgress && <div style={{ ...s.small, color: palette.accent }}>{scanProgress}</div>}
      {scanError && <div style={{ color: palette.danger, fontSize: "0.82rem" }}>{scanError}</div>}
      {scan?.mode === "parameter" && (
        <>
          <div style={s.small}>{scan.strategy}: tested {scan.rows.length} values for {scan.parameter.label || scan.parameter.key}. Results use the currently loaded backtest period.</div>
          <div style={s.grid2}>
            {[ ["Best by Sharpe", bestSharpeScan, "sharpe"], ["Best by Calmar", bestCalmarScan, "calmar"] ].map(([title, row, metric]) => (
              <div key={metric} style={s.panel}>
                <h4 style={{ ...s.h, margin: 0 }}>{title}</h4>
                {row ? (
                  <>
                    <div style={{ fontSize: "0.9rem" }}>{scan.parameter.label || scan.parameter.key} = <strong>{scanParameterLabel(scan.parameter, row.value)}</strong></div>
                    <div style={s.small}>{metric === "sharpe" ? "Sharpe" : "Calmar"}: {fmtNum(row[metric])} · {row.trades} trades</div>
                    <button type="button" style={s.btnGhost} onClick={() => applyScanValue(row.value)}>Use this value</button>
                  </>
                ) : <div style={s.small}>No finite {metric === "sharpe" ? "Sharpe" : "Calmar"} result in this scan.</div>}
              </div>
            ))}
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ borderCollapse: "collapse", fontSize: "0.78rem", width: "100%" }}>
              <thead><tr><th style={thL}>{scan.parameter.label || scan.parameter.key}</th><th style={thR}>Sharpe</th><th style={thR}>Calmar</th><th style={thR}>Trades</th><th style={thR}>Action</th></tr></thead>
              <tbody>
                {scan.rows.map((row, index) => (
                  <tr key={`${String(row.value)}-${index}`} title={row.error || undefined}>
                    <td style={tdL}>{scanParameterLabel(scan.parameter, row.value)}{row === bestSharpeScan ? <span style={{ fontSize: "0.65rem", marginLeft: 6, color: "#5fe0a8" }}>Best Sharpe</span> : null}{row === bestCalmarScan ? <span style={{ fontSize: "0.65rem", marginLeft: 6, color: "#8ec5ff" }}>Best Calmar</span> : null}</td>
                    <td style={tdR}>{fmtNum(row.sharpe)}</td>
                    <td style={tdR}>{fmtNum(row.calmar)}</td>
                    <td style={tdR}>{row.trades}</td>
                    <td style={tdR}>{row.error ? <span style={{ color: palette.danger }}>Failed</span> : <button type="button" style={s.btnGhost} onClick={() => applyScanValue(row.value)}>Use</button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ ...s.small, color: "#a6c8ff" }}>Parameter optimization can overfit this history. Validate the chosen value on a separate time range.</div>
        </>
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: 8, paddingTop: 10, borderTop: `1px solid ${palette.border}` }}>
        <div style={s.row}>
          <strong style={{ fontSize: "0.82rem", marginRight: 4 }}>Walk-forward optimization</strong>
          <label style={s.lbl}>
            Training score
            <select style={s.field} value={walkForwardMetric} disabled={walkForwardRunning} onChange={(event) => setWalkForwardMetric(event.target.value)}>
              <option value="calmar">Calmar</option>
              <option value="sharpe">Sharpe</option>
            </select>
          </label>
          <button type="button" style={{ ...s.btn, padding: "8px 12px" }} onClick={runWalkForward} disabled={!result || !scanParamDef || walkForwardRunning || scanRunning || !!running}>
            {walkForwardRunning ? "Optimizing…" : "Run walk-forward"}
          </button>
        </div>
        <div style={s.small}>For each of 3 expanding training windows, selects the best value from the parameter range above, then tests it on the next unseen window. Limited to 31 values.</div>
        {walkForwardProgress && <div role="status" style={{ ...s.small, color: palette.accent }}>{walkForwardProgress}</div>}
        {walkForwardError && <div role="status" style={{ color: palette.danger, fontSize: "0.82rem" }}>{walkForwardError}</div>}
        {walkForward && (
          <>
            <div style={s.row}>
              <Kpi label="Combined out-of-sample return" value={fmtPct(walkForward.oosMetrics?.totalReturn)} tone={walkForward.oosMetrics?.totalReturn >= 0 ? "good" : "bad"} />
              <Kpi label="Out-of-sample Sharpe" value={fmtNum(walkForward.oosMetrics?.sharpe)} />
              <Kpi label="Out-of-sample Calmar" value={fmtNum(walkForward.oosMetrics?.calmar)} />
              <Kpi label="Test trades" value={String(walkForward.oosMetrics?.trades ?? 0)} />
            </div>
            <div style={{ overflowX: "auto" }}>
              <table style={{ borderCollapse: "collapse", fontSize: "0.76rem", width: "100%" }}>
                <thead><tr><th style={thL}>Fold</th><th style={thR}>Training ends</th><th style={thR}>Chosen value</th><th style={thR}>Train {walkForwardMetric}</th><th style={thR}>Test return</th><th style={thR}>Test trades</th></tr></thead>
                <tbody>{walkForward.folds.map((fold) => <tr key={fold.fold}>
                  <td style={tdL}>{fold.fold}</td>
                  <td style={tdR}>{fmtTime(fold.trainTo).slice(0, 10)}</td>
                  <td style={tdR}>{scanParameterLabel(scanParamDef, fold.bestValue)}</td>
                  <td style={tdR}>{fmtNum(fold.trainScore)}</td>
                  <td style={tdR}>{fmtPct(fold.testMetrics?.totalReturn)}</td>
                  <td style={tdR}>{fold.trades}</td>
                </tr>)}</tbody>
              </table>
            </div>
            <div style={{ ...s.small, color: "#a6c8ff" }}>Parameters are reselected using only each training window. Test results are concatenated and compounded; they remain historical results, not a forecast.</div>
          </>
        )}
      </div>
    </div>
  ) : null;

  return (
    <div style={embedded ? { ...pageStyles.page, minHeight: 0, flex: 1, overflowY: "auto" } : pageStyles.page}>
      {!embedded && <NavBar />}
      <div style={s.body}>
        <div style={s.grid2}>
          {/* ---------------- data + settings ---------------- */}
          <div style={s.panel}>
            <h3 style={s.h}>1. Data</h3>
            <div style={s.row}>
              <input style={{ ...s.field, width: 110 }} value={symbol} onChange={(e) => setSymbol(e.target.value)} placeholder="EURUSD" aria-label="Symbol" />
              <select style={s.field} value={tf} onChange={(e) => setTf(e.target.value)} aria-label="Timeframe">
                {TIMEFRAMES.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
              <input style={{ ...s.field, width: 100 }} type="number" min="50" max={MAX_BACKTEST_BARS} value={limit} onChange={(e) => setLimit(e.target.value)} aria-label="Number of bars, up to 25,000" title="Up to 25,000 candles; older history loads in pages." />
              <button type="button" style={s.btnGhost} onClick={loadCandles} disabled={dataLoading}>
                {dataLoading ? "Loading..." : "Load candles"}
              </button>
              {!isSample && (
                <button type="button" style={s.btnGhost} onClick={switchToSample} disabled={dataLoading}>
                  Use sample candles
                </button>
              )}
            </div>
            <div style={s.small}>{dataNote}</div>

            <h3 style={{ ...s.h, marginTop: 6 }}>2. Money and costs</h3>
            <div style={s.row}>
              {[
                ["capital", "Start capital"],
                ["allocPct", "Position size % of equity"],
                ["spreadBps", "Spread (bps)"],
                ["commissionBps", "Commission (bps/side)"],
                ["slippageBps", "Slippage (bps/side)"],
              ["longFinancingBpsPerDay", "Long carry (bps/day)"],
              ["shortFinancingBpsPerDay", "Short carry (bps/day)"],
              ].map(([key, label]) => (
                <label key={key} style={s.lbl}>
                  {label}
                  <input style={{ ...s.field, width: 120 }} type="number" min={key.includes("Financing") ? -1000 : 0} max={key.includes("Financing") ? 1000 : undefined} step="any" value={settings[key]} onChange={(e) => setSetting(key, e.target.value)} />
                </label>
              ))}
              <label style={s.lbl}>
                Entry order
                <select style={s.field} value={settings.entryOrderType} onChange={(e) => setSetting("entryOrderType", e.target.value)}>
                  <option value="market">Market · next open</option>
                  <option value="limit">Limit · next candle</option>
                  <option value="stop">Stop · next candle</option>
                </select>
              </label>
              <label style={s.lbl}>
                Entry offset (bps)
                <input style={{ ...s.field, width: 120 }} type="number" min="0" max="10000" step="any" disabled={settings.entryOrderType === "market"} value={settings.entryOffsetBps} onChange={(e) => setSetting("entryOffsetBps", e.target.value)} />
              </label>
              <label style={{ ...s.lbl, justifyContent: "flex-end", flexDirection: "row", alignItems: "center", gap: 6, color: palette.text }}>
                <input type="checkbox" checked={!!settings.allowShort} onChange={(e) => setSetting("allowShort", e.target.checked)} />
                Allow shorts
              </label>
            </div>
            <div style={s.small}>1 bp = 0.01%. Limit and stop entries anchor to the signal candle close, stay active for the next candle only, and retry if the signal remains active. Positive carry is charged per elapsed 24 hours; negative values are credits. Position size 100 = fully invested without leverage.</div>

            <h3 style={{ ...s.h, marginTop: 6 }}>Stop loss, take profit and R:R</h3>
            <RiskControl kind="sl" title="Stop loss" units={SL_UNITS} type={settings.slType} value={settings.slValue} onType={(v) => setSetting("slType", v)} onValue={(v) => setSetting("slValue", v)} />
            <RiskControl kind="tp" title="Take profit" units={TP_UNITS} type={settings.tpType} value={settings.tpValue} onType={(v) => setSetting("tpType", v)} onValue={(v) => setSetting("tpValue", v)} disabledUnits={settings.slType === "off" ? ["rr"] : []} />
            <div style={s.row}>
              <label style={{ display: "flex", gap: 6, alignItems: "center", fontSize: "0.82rem", opacity: settings.slType === "off" ? 0.5 : 1 }}>
                <input type="checkbox" disabled={settings.slType === "off"} checked={!!settings.trailing && settings.slType !== "off"} onChange={(e) => setSetting("trailing", e.target.checked)} />
                Trailing stop (follows the best price)
              </label>
              <span style={{ ...s.small, color: palette.accent }}>{rrSummary(settings)}</span>
            </div>
            <div style={s.small}>If one bar touches both the stop and the target, the stop is counted first (cautious). Gaps fill at the open price.</div>
            <label style={{ display: "flex", gap: 6, alignItems: "center", fontSize: "0.82rem" }}>
              <input type="checkbox" checked={live} onChange={(e) => setLive(e.target.checked)} />
              Live dashboard: re-run automatically when you change anything
            </label>
          </div>

          {/* ---------------- strategies: built-in, your Python ones, your rule ones ---------------- */}
          <div style={s.panel}>
            <StrategyLibrary
              catalog={catalog}
              isAdmin={isAdmin}
              items={labItems}
              selected={selected}
              setSelected={setSelected}
              params={params}
              setParam={setParam}
              onRun={runAll}
              running={running}
              error={error}
              onOpenEditor={onOpenEditor}
              adminScanControls={adminScanControls}
            />
          </div>
        </div>

        {!result && (
          <div style={{ ...s.panel, color: palette.mutedText, fontSize: "0.9rem" }}>
            Results appear here: stats table, equity curve with drawdown and trade dots, trades on the price chart, monthly returns and the combined portfolio.
          </div>
        )}

        {result && focus && (
          <>
            {/* ---------------- dashboard cards ---------------- */}
            <div style={s.panel}>
              <div style={{ ...s.row, justifyContent: "space-between" }}>
                <h3 style={s.h}>Dashboard: {focus.name}{live ? " (live)" : ""}</h3>
                <span style={s.small}>
                  {result.cfg.slType !== "off" ? `Stop ${result.cfg.slValue}${UNIT_LABEL[result.cfg.slType]}${result.cfg.trailing ? " trailing" : ""}` : "No stop"} ·{" "}
                  {result.cfg.tpType !== "off" ? `Target ${result.cfg.tpValue}${UNIT_LABEL[result.cfg.tpType]}` : "No target"}
                  {rrSummary(result.cfg) ? ` · ${rrSummary(result.cfg)}` : ""}
                </span>
              </div>
              <div style={{ ...s.row, alignItems: "stretch" }}>
                <Kpi label="Net return" value={fmtPct(focus.metrics.totalReturn)} tone={focus.metrics.totalReturn >= 0 ? "good" : "bad"} sub={`${fmtMoney(focus.metrics.endEquity)} end equity`} />
                <Kpi label="CAGR" value={fmtPct(focus.metrics.cagr)} tone={focus.metrics.cagr >= 0 ? "good" : "bad"} sub={`${fmtNum(focus.metrics.years, 2)} years`} />
                <Kpi label="Sharpe" value={fmtNum(focus.metrics.sharpe)} tone={focus.metrics.sharpe >= 1 ? "good" : focus.metrics.sharpe < 0 ? "bad" : undefined} />
                <Kpi label="Calmar" value={fmtNum(focus.metrics.calmar)} tone={focus.metrics.calmar >= 1 ? "good" : focus.metrics.calmar < 0 ? "bad" : undefined} />
                <Kpi label="Max drawdown" value={fmtPct(focus.metrics.maxDrawdown)} tone="bad" sub={`${fmtNum(focus.metrics.longestDrawdownDays, 0)} days longest`} />
                <Kpi label="Win rate" value={fmtPct(focus.metrics.winRate, 1)} sub={`${focus.metrics.trades} trades`} />
                <Kpi label="Profit factor" value={fmtNum(focus.metrics.profitFactor)} tone={focus.metrics.profitFactor >= 1 ? "good" : "bad"} />
                <Kpi label="Average R" value={Number.isFinite(focus.metrics.avgR) ? fmtNum(focus.metrics.avgR) : "-"} tone={focus.metrics.avgR > 0 ? "good" : focus.metrics.avgR < 0 ? "bad" : undefined} sub={Number.isFinite(focus.metrics.avgR) ? "per trade, in risk units" : "turn on a stop loss"} />
              </div>
            </div>

            <div style={s.panel}>
              <div style={{ ...s.row, justifyContent: "space-between" }}>
                <div>
                  <h3 style={s.h}>Out-of-sample validation: {focus.name}</h3>
                  <div style={s.small}>Same strategy settings, run flat on each period. The first test candle is reserved for indicator warm-up.</div>
                </div>
                <label style={s.lbl}>
                  In-sample share
                  <select style={s.field} value={validationTrainPct} onChange={(event) => setValidationTrainPct(Number(event.target.value))}>
                    {[60, 70, 80].map((value) => <option key={value} value={value}>{value}% train / {100 - value}% test</option>)}
                  </select>
                </label>
                <button type="button" style={s.btnGhost} onClick={runValidation} disabled={validationBusy}>
                  {validationBusy ? "Checking…" : "Run split check"}
                </button>
              </div>
              {validation && (
                <div style={s.grid2}>
                  {[
                    ["In sample", validation.trainMetrics, result.candles[0].time, result.candles[validation.splitIndex - 1].time],
                    ["Out of sample", validation.testMetrics, validation.splitTime, result.candles[result.candles.length - 1].time],
                  ].map(([label, metrics, from, to]) => (
                    <div key={label} style={{ ...s.panel, padding: 12, gap: 8 }}>
                      <div style={s.row}>
                        <strong style={{ fontSize: "0.84rem" }}>{label}</strong>
                        <span style={s.small}>{fmtTime(from).slice(0, 10)} – {fmtTime(to).slice(0, 10)}</span>
                      </div>
                      <div style={{ ...s.row, alignItems: "stretch" }}>
                        <Kpi label="Net return" value={metrics ? fmtPct(metrics.totalReturn) : "—"} tone={metrics?.totalReturn >= 0 ? "good" : "bad"} />
                        <Kpi label="Sharpe" value={metrics ? fmtNum(metrics.sharpe) : "—"} />
                        <Kpi label="Max drawdown" value={metrics ? fmtPct(metrics.maxDrawdown) : "—"} tone="bad" />
                        <Kpi label="Trades" value={metrics ? String(metrics.trades) : "—"} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {!validation && !validationError && <div style={s.small}>Run the split check to compare performance on earlier and later candles.</div>}
              {validationError && <div role="status" style={{ color: palette.danger, fontSize: "0.8rem" }}>{validationError}</div>}
              <div style={{ ...s.small, color: "#a6c8ff" }}>This split is a diagnostic. Choosing parameters after inspecting the test period can still overfit it.</div>
            </div>

            <div style={s.panel}>
              <div style={{ ...s.row, justifyContent: "space-between" }}>
                <div>
                  <h3 style={s.h}>Monte Carlo trade-order check: {focus.name}</h3>
                  <div style={s.small}>Resamples the realized trade returns with replacement to show outcome sensitivity to trade order.</div>
                </div>
                <button type="button" style={s.btnGhost} onClick={runMonteCarlo} disabled={monteCarloBusy || (focus.trades || []).length < 2}>
                  {monteCarloBusy ? "Simulating…" : "Run 500 paths"}
                </button>
              </div>
              {monteCarlo ? (
                <div style={{ ...s.row, alignItems: "stretch" }}>
                  <Kpi label="5th percentile equity" value={fmtMoney(monteCarlo.p05Equity)} tone={monteCarlo.p05Equity >= result.cfg.capital ? "good" : "bad"} />
                  <Kpi label="Median equity" value={fmtMoney(monteCarlo.medianEquity)} />
                  <Kpi label="95th percentile equity" value={fmtMoney(monteCarlo.p95Equity)} />
                  <Kpi label="Paths below start" value={fmtPct(monteCarlo.probabilityOfLoss, 1)} tone={monteCarlo.probabilityOfLoss > 0.5 ? "bad" : undefined} />
                  <Kpi label="Worst 5% drawdown" value={fmtPct(monteCarlo.p05Drawdown)} tone="bad" />
                </div>
              ) : <div style={s.small}>{(focus.trades || []).length < 2 ? "At least two closed trades are needed." : "Run the simulation to compare 500 reshuffled trade sequences."}</div>}
              <div style={s.small}>This bootstrap preserves the observed trade returns but ignores market path, overlapping positions, and new slippage or spread changes. It is a robustness check, not a forecast.</div>
            </div>

            {/* ---------------- stats ---------------- */}
            <div style={s.panel}>
              <div style={{ ...s.row, justifyContent: "space-between" }}>
                <h3 style={s.h}>Results: {result.symbol} {result.tf}, {result.candles.length} bars, starting capital {fmtMoney(result.cfg.capital)}</h3>
                <span style={s.small}>Click a column title to focus the charts on it.</span>
              </div>
              <div style={{ overflowX: "auto" }}>
                <table style={{ borderCollapse: "collapse", fontSize: "0.8rem", width: "100%", minWidth: 520 }}>
                  <thead>
                    <tr>
                      <th style={thL}></th>
                      {columns.map((c) => (
                        <th key={c.id} style={{ ...thR, color: c.color, cursor: c.id === "buyhold" ? "default" : "pointer", background: focus.id === c.id ? "rgba(242, 243, 245,0.10)" : "transparent" }} onClick={() => c.id !== "buyhold" && setFocusId(c.id)}>
                          {c.name}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {STAT_ROWS.map(([label, fmt, signFn]) => (
                      <tr key={label}>
                        <td style={tdL}>{label}</td>
                        {columns.map((c) => {
                          const m = c.metrics;
                          const val = m ? fmt(m) : "-";
                          const sign = m && signFn ? signFn(m) : 0;
                          return (
                            <td key={c.id} style={{ ...tdR, background: focus.id === c.id ? "rgba(242, 243, 245,0.06)" : "transparent", color: signFn ? (sign > 0 ? "#5fe0a8" : sign < 0 ? "#ff7d8c" : palette.text) : palette.text }}>
                              {val}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={s.small}>
                Sharpe and Sortino use daily returns, annualised. Calmar = CAGR divided by the largest drawdown. Past results do not predict the future; this is a research tool, not advice.
              </div>
            </div>

            {/* ---------------- equity / drawdown / dots ---------------- */}
            <div style={s.panel}>
              <div style={{ ...s.row, justifyContent: "space-between" }}>
                <h3 style={s.h}>Equity curve, drawdown and trade dots: {focus.name}</h3>
                <div style={s.row}>
                  {[...(result.portfolio ? [result.portfolio] : []), ...result.runs].map((c) => (
                    <button key={c.id} type="button" onClick={() => setFocusId(c.id)} style={{ ...s.btnGhost, padding: "3px 9px", fontSize: "0.72rem", borderColor: focus.id === c.id ? c.color : palette.border, color: c.color }}>
                      {c.name}
                    </button>
                  ))}
                </div>
              </div>
              <EquityChart curves={curves} focus={focus} capital={result.cfg.capital} />
            </div>

            {/* ---------------- trades on price chart ---------------- */}
            <div style={s.panel}>
              <div style={{ ...s.row, justifyContent: "space-between" }}>
                <h3 style={s.h}>Trades on the price chart: {focus.name} ({(focus.trades || []).length} trades)</h3>
                <div style={s.row}>
                  {result.sample ? (
                    <span style={s.small}>Load real candles to see these trades on the main chart.</span>
                  ) : (
                    <>
                      <label style={{ display: "flex", gap: 6, alignItems: "center", fontSize: "0.8rem" }}>
                        <input type="checkbox" checked={showOnMain} onChange={(e) => setShowOnMain(e.target.checked)} />
                        Keep these trades on the main chart
                      </label>
                      <button type="button" style={{ ...s.btn, padding: "8px 14px", fontSize: "0.85rem" }} onClick={openOnMainChart}>
                        Show on main chart ({result.symbol} {result.tf})
                      </button>
                    </>
                  )}
                </div>
              </div>
              <TradesPriceChart candles={result.candles} trades={focus.trades} zoomTo={zoomTrade} />
              <div style={s.small}>▲ BUY = long entry · ▼ SHORT = short entry · Circle = exit. TP = target, SL = stop, SELL/COVER = strategy exit, CLOSE = end of data. Green exit = profit; red exit = loss. Click a trade row below to zoom in and reveal that trade’s labels.</div>
              {zoomTrade && (
                <div style={{ ...s.row, padding: "8px 10px", border: `1px solid ${palette.border}`, borderRadius: 8, background: "rgba(255,255,255,0.025)" }} role="status">
                  <strong style={{ color: zoomTrade.side === 1 ? "#5fe0a8" : "#ff7d8c" }}>{zoomTrade.side === 1 ? "LONG" : "SHORT"} trade</strong>
                  <span style={s.small}>Entry {fmtTime(zoomTrade.entryTime)} @ {Number(zoomTrade.entryPrice).toPrecision(6)}</span>
                  <span style={s.small}>Exit {fmtTime(zoomTrade.exitTime)} @ {Number(zoomTrade.exitPrice).toPrecision(6)}</span>
                  <strong style={{ color: zoomTrade.pnl > 0 ? "#5fe0a8" : "#ff7d8c" }}>{fmtPct(zoomTrade.ret)} · {fmtMoney(zoomTrade.pnl)}</strong>
                  <span style={s.small}>Reason: {REASON_INFO[zoomTrade.reason]?.label || zoomTrade.reason}</span>
                </div>
              )}
            </div>

            <div style={s.grid2}>
              <div style={s.panel}>
                <h3 style={s.h}>Monthly returns: {focus.name}</h3>
                <MonthlyTable months={focus.months} />
              </div>
              <div style={s.panel}>
                <h3 style={s.h}>Trade return distribution: {focus.name}</h3>
                <ReturnHistogram bins={bins} />
              </div>
            </div>

            <div style={s.grid2}>
              <div style={s.panel}>
                <h3 style={s.h}>How trades ended: {focus.name}</h3>
                <ExitReasons metrics={focus.metrics} total={(focus.trades || []).length} />
                <div style={s.small}>Stop and take-profit exits only appear when you switch them on above.</div>
              </div>
              <div style={s.panel}>
                <h3 style={s.h}>R-multiple distribution: {focus.name}</h3>
                {Number.isFinite(focus.metrics.avgR) ? (
                  <>
                    <ReturnHistogram bins={rBins} unit="R" />
                    <div style={s.small}>1R = the distance to your stop loss. Average {fmtNum(focus.metrics.avgR)}R, best {fmtNum(focus.metrics.bestR)}R, worst {fmtNum(focus.metrics.worstR)}R.</div>
                  </>
                ) : (
                  <div style={s.small}>Turn on a stop loss to measure every trade in R (risk units).</div>
                )}
              </div>
            </div>

            {attribution && (
              <>
                <div style={s.grid2}>
                  <AttributionTable title={`Trades by UTC session: ${focus.name}`} rows={attribution.sessions} />
                  <AttributionTable title={`Trades by Kaufman regime: ${focus.name}`} rows={attribution.regimes} />
                  <AttributionTable title={`Trades by HMM regime: ${focus.name}`} rows={attribution.hmmRegimes} />
                </div>
                <div style={s.small}>Sessions use fixed UTC hour bands. Kaufman efficiency {attribution.period}/{attribution.smooth} classifies the signal candle: Range ≤ {attribution.rangeThreshold}, Trend ≥ {attribution.trendThreshold}, otherwise Mixed. HMM status: {attribution.hmm.status}{attribution.hmm.status === "ready" ? `; lookback ${attribution.hmm.lookback}, fit on the first 70%, forward-filtered on the final 30% (${attribution.hmm.validationBars} holdout bars).` : ` — ${attribution.hmm.reason || "not available for this candle history"}`} Trades before the holdout are labeled Training window (not scored). The HMM score is a data-fit diagnostic, not trading performance.</div>
              </>
            )}

            <div style={s.panel}>
              <h3 style={s.h}>Rolling performance: {focus.name}</h3>
              <div style={{ overflowX: "auto" }}>
                <table style={{ borderCollapse: "collapse", fontSize: "0.76rem", width: "100%" }}>
                  <thead><tr><th style={thL}>Window</th><th style={thR}>Return</th><th style={thR}>Max drawdown</th><th style={thR}>Annualized volatility</th><th style={thR}>Sharpe</th><th style={thR}>Daily closes</th></tr></thead>
                  <tbody>{rolling.map((window) => <tr key={window.windowDays}>
                    <td style={tdL}>{window.windowDays} days ({window.actualDays} available)</td>
                    <td style={tdR}>{fmtPct(window.totalReturn)}</td>
                    <td style={tdR}>{fmtPct(window.maxDrawdown)}</td>
                    <td style={tdR}>{fmtPct(window.annualizedVolatility)}</td>
                    <td style={tdR}>{fmtNum(window.sharpe)}</td>
                    <td style={tdR}>{window.observations}</td>
                  </tr>)}</tbody>
                </table>
              </div>
              <div style={s.small}>Rolling windows use available daily closes and may be shorter than the selected period when the loaded history is limited.</div>
            </div>

            <div style={s.panel}>
              <h3 style={s.h}>Find a good stop and target: scan the grid</h3>
              <div style={s.row}>
                <label style={s.lbl}>
                  Strategy
                  <select style={s.field} value={scanStrategyId} disabled={scanRunning} onChange={(e) => {
                    setScanStrategy(e.target.value);
                    setScanParamKey("");
                    setScanFrom("");
                    setScanTo("");
                    setScanStep("");
                    setScan(null);
                    setScanError("");
                  }}>
                    {result.runs.map((r) => (
                      <option key={r.id} value={r.id}>{r.name}</option>
                    ))}
                  </select>
                </label>
              </div>
              <>
                  <div style={s.row}>
                    <label style={s.lbl}>
                      Measure
                      <select style={s.field} value={scanMetric} onChange={(e) => setScanMetric(e.target.value)}>
                        {SCAN_METRICS.map(([id, label]) => (
                          <option key={id} value={id}>{label}</option>
                        ))}
                      </select>
                    </label>
                    <label style={s.lbl}>
                      Stop units
                      <select style={s.field} value={scanSl} onChange={(e) => setScanSl(e.target.value)}>
                        <option value="atr">ATR multiples</option>
                        <option value="pct">Percent</option>
                      </select>
                    </label>
                    <label style={s.lbl}>
                      Target units
                      <select style={s.field} value={scanTp} onChange={(e) => setScanTp(e.target.value)}>
                        <option value="rr">R:R (reward : risk)</option>
                        <option value="atr">ATR multiples</option>
                        <option value="pct">Percent</option>
                      </select>
                    </label>
                    <button type="button" style={{ ...s.btn, alignSelf: "flex-end" }} onClick={runScan} disabled={scanRunning}>Scan</button>
                  </div>
                  {scan?.mode === "risk" && (
                    <>
                      <div style={s.small}>
                        {scan.strategy}: {SCAN_METRICS.find((m) => m[0] === scan.metric)[1]} for each stop (rows) and target (columns). Green is better. Click a cell to apply it to the whole dashboard.
                      </div>
                      <ScanGrid
                        scan={scan}
                        current={settings.slType === scan.slUnit || settings.slType === "off" ? { sl: settings.slType === "off" ? 0 : Number(settings.slValue), tp: settings.tpType === scan.tpUnit ? Number(settings.tpValue) : settings.tpType === "off" ? 0 : -1 } : null}
                        onPick={(sl, tp) =>
                          setSettings((prev) => ({
                            ...prev,
                            slType: sl > 0 ? scan.slUnit : "off",
                            slValue: sl > 0 ? sl : prev.slValue,
                            tpType: tp > 0 ? scan.tpUnit : "off",
                            tpValue: tp > 0 ? tp : prev.tpValue,
                          }))
                        }
                      />
                      <div style={{ ...s.small, color: "#a6c8ff" }}>
                        The best cell can reflect luck on this data. Check broad patterns and test a different period before relying on it.
                      </div>
                    </>
                  )}
              </>

            </div>

            {result.corr && (
              <div style={s.panel}>
                <h3 style={s.h}>Portfolio: equal weight, rebalanced every bar. Correlation of daily returns</h3>
                <div style={{ overflowX: "auto" }}>
                  <table style={{ borderCollapse: "collapse", fontSize: "0.78rem" }}>
                    <thead>
                      <tr>
                        <th style={thL}></th>
                        {result.runs.map((r) => (
                          <th key={r.id} style={{ ...thR, color: r.color }}>{r.name}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {result.runs.map((r, i) => (
                        <tr key={r.id}>
                          <td style={{ ...tdL, color: r.color }}>{r.name}</td>
                          {result.corr[i].map((v, j) => {
                            const a = Number.isFinite(v) ? Math.abs(v) * 0.5 : 0;
                            return (
                              <td key={j} style={{ ...tdR, textAlign: "center", background: v >= 0 ? `rgba(242, 243, 245,${a})` : `rgba(255, 125, 140,${a})` }}>
                                {fmtNum(v)}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div style={s.small}>Low or negative correlation between strategies is what makes the combined portfolio smoother than each one alone.</div>
              </div>
            )}

            {/* ---------------- trade list ---------------- */}
            <div style={s.panel}>
              <div style={{ ...s.row, justifyContent: "space-between" }}>
                <h3 style={s.h}>Trade list: {focus.name} (newest 300 of {(focus.trades || []).length})</h3>
                <button type="button" style={s.btnGhost} onClick={downloadCsv}>Download all trades (CSV)</button>
              </div>
              <div style={{ overflow: "auto", maxHeight: 360 }}>
                <table style={{ borderCollapse: "collapse", fontSize: "0.75rem", width: "100%", minWidth: 640 }}>
                  <thead>
                    <tr>
                      {["Strategy", "Side", "Entry", "Entry price", "Exit", "Exit price", "Return", "R", "P&L", "Bars", "Exit"].map((h) => (
                        <th key={h} style={{ ...thR, position: "sticky", top: 0, background: palette.panel }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {tradeRows.map((t, i) => (
                      <tr key={i} onClick={() => setZoomTrade(t)} style={{ cursor: "pointer" }}>
                        <td style={tdR}>{t.strategy || focus.name}</td>
                        <td style={{ ...tdR, color: t.side === 1 ? "#5fe0a8" : "#ff7d8c" }}>{t.side === 1 ? "Long" : "Short"}</td>
                        <td style={tdR}>{fmtTime(t.entryTime)}</td>
                        <td style={tdR}>{t.entryPrice}</td>
                        <td style={tdR}>{fmtTime(t.exitTime)}</td>
                        <td style={tdR}>{Number(t.exitPrice).toPrecision(6)}</td>
                        <td style={{ ...tdR, color: t.pnl > 0 ? "#5fe0a8" : "#ff7d8c" }}>{fmtPct(t.ret)}</td>
                        <td style={tdR}>{Number.isFinite(t.rMultiple) ? fmtNum(t.rMultiple) : "-"}</td>
                        <td style={{ ...tdR, color: t.pnl > 0 ? "#5fe0a8" : "#ff7d8c" }}>{fmtMoney(t.pnl)}</td>
                        <td style={tdR}>{t.bars}</td>
                        <td style={{ ...tdR, color: (REASON_INFO[t.reason] || {}).color }}>{t.reason === "target" ? "TP" : t.reason === "stop" ? "SL" : t.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const thL = { padding: "6px 8px", textAlign: "left", borderBottom: `1px solid ${palette.border}`, color: palette.mutedText, fontWeight: 500 };
const thR = { padding: "6px 8px", textAlign: "right", borderBottom: `1px solid ${palette.border}`, fontWeight: 600, whiteSpace: "nowrap" };
const tdL = { padding: "5px 8px", borderBottom: `1px solid ${palette.border}`, color: palette.mutedText, whiteSpace: "nowrap" };
const tdR = { padding: "5px 8px", borderBottom: `1px solid ${palette.border}`, textAlign: "right", whiteSpace: "nowrap" };

/** Segmented unit buttons + slider + number box for a stop loss or a take profit. */
function RiskControl({ kind, title, units, type, value, onType, onValue, disabledUnits = [] }) {
  const range = SLIDER[kind][type];
  const on = type !== "off";
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6, padding: "8px 10px", border: `1px solid ${palette.border}`, borderRadius: 8 }}>
      <div style={{ ...s.row, justifyContent: "space-between" }}>
        <span style={{ fontSize: "0.85rem", fontWeight: 600 }}>{title}</span>
        <div style={{ display: "flex", gap: 4 }}>
          {units.map(([id, label]) => {
            const disabled = disabledUnits.includes(id);
            return (
              <button
                key={id}
                type="button"
                disabled={disabled}
                title={disabled ? "Needs a stop loss" : ""}
                onClick={() => {
                  onType(id);
                  const r = SLIDER[kind][id];
                  if (r && id !== type) onValue(kind === "sl" ? (id === "atr" ? 2 : 0.3) : id === "rr" ? 2 : id === "atr" ? 4 : 0.6);
                }}
                style={{
                  ...s.btnGhost,
                  padding: "3px 9px",
                  fontSize: "0.72rem",
                  opacity: disabled ? 0.4 : 1,
                  background: type === id ? "rgba(242, 243, 245,0.18)" : "transparent",
                  borderColor: type === id ? palette.accent : palette.border,
                  color: type === id ? palette.accent : palette.text,
                }}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>
      {on && range && (
        <div style={s.row}>
          <input
            type="range"
            aria-label={`${title} size`}
            min={range[0]}
            max={range[1]}
            step={range[2]}
            value={Math.min(Math.max(Number(value) || range[0], range[0]), range[1])}
            onChange={(e) => onValue(Number(e.target.value))}
            style={{ flex: "1 1 160px", accentColor: palette.accent }}
          />
          <input style={{ ...s.field, width: 76 }} type="number" min="0" step="any" value={value} onChange={(e) => onValue(e.target.value)} aria-label={`${title} value`} />
          <span style={s.small}>{UNIT_LABEL[type]}</span>
        </div>
      )}
    </div>
  );
}

function rrSummary(settings) {
  const sl = Number(settings.slValue);
  const tp = Number(settings.tpValue);
  if (settings.slType === "off" || settings.tpType === "off" || !(sl > 0) || !(tp > 0)) return "";
  if (settings.tpType === "rr") return `Reward : risk = ${tp} : 1`;
  if (settings.tpType === settings.slType) return `Reward : risk = ${(tp / sl).toFixed(2)} : 1`;
  return "";
}

const REASON_INFO = {
  target: { label: "Take profit hit", color: "#5fe0a8" },
  stop: { label: "Stop loss hit", color: "#ff7d8c" },
  signal: { label: "Strategy exit / flip", color: "#a497e6" },
  end: { label: "Still open at the end", color: "#9097a3" },
};

function ExitReasons({ metrics, total }) {
  if (!metrics || !total) return <div style={s.small}>No trades.</div>;
  const order = ["target", "stop", "signal", "end"].filter((k) => metrics.reasons[k]);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {order.map((k) => {
        const r = metrics.reasons[k];
        const share = r.count / total;
        return (
          <div key={k}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.8rem" }}>
              <span style={{ color: REASON_INFO[k].color }}>{REASON_INFO[k].label}</span>
              <span>{r.count} trades ({fmtPct(share, 0)}) · avg {fmtPct(r.ret / r.count, 3)}</span>
            </div>
            <div style={{ height: 8, background: "#050608", borderRadius: 4, overflow: "hidden" }}>
              <div style={{ width: `${share * 100}%`, height: "100%", background: REASON_INFO[k].color }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Kpi({ label, value, tone, sub }) {
  return (
    <div style={{ flex: "1 1 120px", minWidth: 110, background: "linear-gradient(145deg, rgba(228,244,255,0.10), rgba(131,166,201,0.035))", border: "1px solid rgba(211,235,255,0.14)", borderRadius: 15, boxShadow: "inset 0 1px 0 rgba(255,255,255,0.05)", padding: "12px 14px", backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)" }}>
      <div style={{ fontSize: "0.7rem", color: palette.mutedText, textTransform: "uppercase", letterSpacing: "0.04em" }}>{label}</div>
      <div style={{ fontSize: "1.25rem", fontWeight: 700, color: tone === "good" ? "#5fe0a8" : tone === "bad" ? "#ff7d8c" : palette.text }}>{value}</div>
      {sub && <div style={{ fontSize: "0.7rem", color: palette.mutedText }}>{sub}</div>}
    </div>
  );
}

function AttributionTable({ title, rows }) {
  return (
    <div style={s.panel}>
      <h3 style={s.h}>{title}</h3>
      {rows.length ? (
        <div style={{ overflowX: "auto" }}>
          <table style={{ borderCollapse: "collapse", fontSize: "0.76rem", width: "100%" }}>
            <thead><tr><th style={thL}>Group</th><th style={thR}>Trades</th><th style={thR}>Win rate</th><th style={thR}>Net P&amp;L</th><th style={thR}>Profit factor</th></tr></thead>
            <tbody>{rows.map((row) => <tr key={row.name}>
              <td style={tdL}>{row.name}</td>
              <td style={tdR}>{row.trades}</td>
              <td style={tdR}>{fmtPct(row.winRate, 1)}</td>
              <td style={{ ...tdR, color: row.totalPnl >= 0 ? "#5fe0a8" : "#ff7d8c" }}>{fmtMoney(row.totalPnl)}</td>
              <td style={tdR}>{fmtNum(row.profitFactor)}</td>
            </tr>)}</tbody>
          </table>
        </div>
      ) : <div style={s.small}>No closed trades to group.</div>}
    </div>
  );
}

/** Stop-loss x take-profit grid for one strategy; click a cell to use those settings. */
function ScanGrid({ scan, current, onPick }) {
  const fmt = (v) => (v === null ? "-" : PCT_METRICS.has(scan.metric) ? fmtPct(v, 1) : fmtNum(v));
  const flat = scan.cells.flat().filter((v) => v !== null);
  const lo = Math.min(...flat);
  const hi = Math.max(...flat);
  const colour = (v) => {
    if (v === null) return "transparent";
    const x = hi === lo ? 0.5 : (v - lo) / (hi - lo);
    return x >= 0.5 ? `rgba(95, 224, 168,${(x - 0.5) * 1.2 + 0.08})` : `rgba(255, 125, 140,${(0.5 - x) * 1.2 + 0.08})`;
  };
  const head = (v, unit) => (v === 0 ? "off" : `${v}${unit === "pct" ? "%" : unit === "rr" ? "R" : "×"}`);
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ borderCollapse: "collapse", fontSize: "0.75rem" }}>
        <thead>
          <tr>
            <th style={{ ...thL, fontSize: "0.7rem" }}>Stop ↓ / Target →</th>
            {scan.tpVals.map((tp) => (
              <th key={tp} style={{ ...thR, textAlign: "center" }}>{head(tp, scan.tpUnit)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {scan.slVals.map((sl, i) => (
            <tr key={sl}>
              <td style={{ ...tdL, fontWeight: 600 }}>{head(sl, scan.slUnit)}</td>
              {scan.tpVals.map((tp, j) => {
                const v = scan.cells[i][j];
                const isCurrent = current && current.sl === sl && current.tp === tp;
                return (
                  <td
                    key={tp}
                    onClick={() => v !== null && onPick(sl, tp)}
                    title={v === null ? "Needs a stop loss" : "Click to use these settings"}
                    style={{ ...tdR, textAlign: "center", cursor: v === null ? "default" : "pointer", background: colour(v), outline: isCurrent ? `2px solid ${palette.accent}` : "none", outlineOffset: -2 }}
                  >
                    {fmt(v)}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
